import { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  Briefcase,
  Calendar,
  ChevronDown,
  ChevronUp,
  Database,
  Home,
  Sparkles,
  TrendingUp,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ChaoServiceDef } from "@/services/chaoDeObra";
import { cn } from "@/utils/cn";
import { buildEapCatalogSections, type EapCatalogSection } from "./eapCatalogTree";
import {
  CATEGORY_THEMES,
  STATUS_LABELS,
  formatLastUpdate,
  getServiceStatus,
  groupCode,
  groupTitle,
  sectionLeaves,
  serviceLabel,
  weightedPct,
  type ServiceStatus,
} from "./eapProgressUtils";

function pickServiceIcon(name: string): LucideIcon {
  const n = name.toLowerCase();
  if (n.includes("limpeza")) return Sparkles;
  if (n.includes("locação") || n.includes("locacao") || n.includes("casa")) return Home;
  return Wrench;
}

function StatusBadge({ status }: { status: ServiceStatus }) {
  const styles: Record<ServiceStatus, string> = {
    nao_iniciado: "bg-slate-100 text-slate-600",
    atencao: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
    em_andamento: "bg-sky-50 text-sky-800 ring-1 ring-sky-200",
    concluido: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
  };
  const dots: Record<ServiceStatus, string> = {
    nao_iniciado: "bg-slate-400",
    atencao: "bg-amber-500",
    em_andamento: "bg-sky-500",
    concluido: "bg-emerald-500",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold",
        styles[status],
      )}
    >
      <span className={cn("w-1.5 h-1.5 rounded-full", dots[status])} />
      {STATUS_LABELS[status]}
    </span>
  );
}

function ProgressBar({ pct, barClass }: { pct: number; barClass: string }) {
  return (
    <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
      <div
        className={cn("h-full rounded-full transition-all duration-300", barClass)}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

function ReadOnlyServiceRow({
  svc,
  pct,
  theme,
}: {
  svc: ChaoServiceDef;
  pct: number;
  theme: (typeof CATEGORY_THEMES)[number];
}) {
  const Icon = pickServiceIcon(svc.name);
  const status = getServiceStatus(pct);

  return (
    <div className="flex flex-col gap-3 py-4 px-4 sm:px-5 border-t border-slate-100 bg-white first:border-t-0">
      <div className="flex items-start gap-3 min-w-0">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", theme.light)}>
          <Icon className={cn("w-5 h-5", theme.text)} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800 leading-snug">{serviceLabel(svc.name)}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={cn("text-sm font-bold tabular-nums px-2.5 py-1 rounded-lg", theme.light, theme.text)}>
            {Math.round(pct)}%
          </span>
          <StatusBadge status={status} />
        </div>
      </div>
      <div className="pl-[52px] pr-1">
        <ProgressBar pct={pct} barClass={theme.bar} />
      </div>
    </div>
  );
}

function CategoryAccordionReadOnly({
  section,
  index,
  pctMap,
  expanded,
  onToggle,
}: {
  section: EapCatalogSection;
  index: number;
  pctMap: Record<string, number>;
  expanded: boolean;
  onToggle: () => void;
}) {
  const theme = CATEGORY_THEMES[index % CATEGORY_THEMES.length];
  const leaves = sectionLeaves(section);
  const pct = weightedPct(leaves, pctMap);
  const allRows: { svc: ChaoServiceDef; subgroup?: string }[] = [
    ...section.items.map((svc) => ({ svc })),
    ...section.subgroups.flatMap((sg) =>
      sg.items.map((svc) => ({ svc, subgroup: groupTitle(sg.group) })),
    ),
  ];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex flex-col lg:flex-row lg:items-center gap-4 p-4 sm:p-5 text-left hover:bg-slate-50/80 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span
            className={cn(
              "w-11 h-11 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0",
              theme.num,
            )}
          >
            {groupCode(section.group)}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-bold text-slate-900 tracking-wide">{groupTitle(section.group)}</p>
            <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              {leaves.length} serviço{leaves.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4 lg:gap-6 flex-1 lg:justify-end">
          <div className="flex-1 min-w-[120px] max-w-md hidden sm:block">
            <div className="flex justify-between items-baseline mb-1">
              <span className={cn("text-2xl font-black tabular-nums", theme.text)}>{pct}%</span>
            </div>
            <ProgressBar pct={pct} barClass={theme.bar} />
          </div>
          <span className={cn("text-2xl font-black tabular-nums sm:hidden", theme.text)}>{pct}%</span>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
          )}
        </div>
      </button>

      {expanded && allRows.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/40">
          {allRows.map(({ svc, subgroup }, rowIdx) => {
            const prevSubgroup = rowIdx > 0 ? allRows[rowIdx - 1].subgroup : undefined;
            const showSubgroup = subgroup && subgroup !== prevSubgroup;
            return (
              <div key={svc.id}>
                {showSubgroup && (
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-5 pt-3 pb-0">
                    {subgroup}
                  </p>
                )}
                <ReadOnlyServiceRow svc={svc} pct={pctMap[svc.id] ?? 0} theme={theme} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface Props {
  catalog: ChaoServiceDef[];
  pctMap: Record<string, number>;
  generalPct: number;
  lastUpdateAt?: string | null;
  lastUpdateBy?: string | null;
}

export function EapReportProgressCards({
  catalog,
  pctMap,
  generalPct,
  lastUpdateAt,
  lastUpdateBy,
}: Props) {
  const sections = useMemo(() => buildEapCatalogSections(catalog), [catalog]);
  const leaves = useMemo(() => sections.flatMap(sectionLeaves), [sections]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const sectionsKeyRef = useRef("");

  useEffect(() => {
    const key = sections.map((s) => s.group.id).join("|");
    if (!key) {
      sectionsKeyRef.current = "";
      setExpanded(new Set());
      return;
    }
    if (key !== sectionsKeyRef.current) {
      sectionsKeyRef.current = key;
      setExpanded(new Set());
    }
  }, [sections]);

  const stats = useMemo(() => {
    const counts = { concluido: 0, em_andamento: 0, atencao: 0, nao_iniciado: 0 };
    for (const l of leaves) {
      counts[getServiceStatus(pctMap[l.id] ?? 0)]++;
    }
    return { counts };
  }, [leaves, pctMap]);

  if (sections.length === 0) {
    return (
      <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-200 px-4 py-3">
        Avanço físico não registrado neste relatório.
      </p>
    );
  }

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-teal-600" />
          Avanço físico por serviço
        </h3>
        <p className="text-sm text-slate-500 mt-1">
          Percentuais registrados neste relatório. Expanda cada etapa para ver as contas filhas.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-teal-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-500">Progresso geral</p>
              <p className="text-3xl font-black text-teal-600 tabular-nums mt-0.5">{generalPct}%</p>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-teal-500 transition-all"
                  style={{ width: `${Math.min(100, generalPct)}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-50 flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5 text-sky-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-500">Serviços totais</p>
              <p className="text-3xl font-black text-slate-900 mt-0.5">{leaves.length}</p>
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-3 text-[11px] font-medium text-slate-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" />
                  {stats.counts.concluido} Concluídos
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-sky-500" />
                  {stats.counts.em_andamento} Em andamento
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                  {stats.counts.atencao} Atenção
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-slate-400" />
                  {stats.counts.nao_iniciado} Não iniciados
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-rose-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-500">Última atualização</p>
              <p className="text-xl font-black text-slate-900 mt-1">{formatLastUpdate(lastUpdateAt ?? undefined)}</p>
              <p className="text-[11px] text-slate-500 mt-2">Por {lastUpdateBy ?? "—"}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {sections.map((section, i) => (
          <CategoryAccordionReadOnly
            key={section.group.id}
            section={section}
            index={i}
            pctMap={pctMap}
            expanded={expanded.has(section.group.id)}
            onToggle={() => toggle(section.group.id)}
          />
        ))}
      </div>
    </div>
  );
}
